import TextArea from '@hero/ui-kit/inputs/TextArea';
import DefaultSelect from '@hero/ui-kit/inputs/DefaultSelect';
import DatePicker from '@hero/ui-kit/components/DatePicker';
import React from 'react';
import Label from '@hero/ui-kit/typography/Label';
import Section from '@hero/ui-kit/layout/Section';
import { AllApprovalStatusSubStates, ApprovalStatus, LeadStateResponse } from '../../types';
import getApprovalStatus from '../../utils/getApprovalStatus';
import getApprovalSubStatus from '../../utils/getApprovalSubStatus';
import { useFormContext, useWatch, Controller } from '@hero/ui-kit/inputs/Form';
import * as Style from './style.module.scss';
import { format, setHours, setMinutes, isToday } from 'date-fns';
import Button from '@hero/ui-kit/inputs/Button';

interface FormInputsProps {
    state?: LeadStateResponse;
    isSubmitDisabled?: boolean;
    requiredDateTimeStates?: string[];
}

const FormInputs: React.FC<FormInputsProps> = ({
    state,
    isSubmitDisabled = false,
    requiredDateTimeStates = []
}) => {
    const chosenActivityType = useWatch({ name: 'activity_type' });
    const {
        control,
        formState: { isValid }
    } = useFormContext();

    const [timeDate, setTimeDate] = React.useState<Date | undefined>();
    const [date, setDate] = React.useState<Date | undefined>();

    const currentStateStatus = state?.current_state.toUpperCase() as ApprovalStatus;

    const showDateAndTimeFields = React.useMemo(() => {
        return requiredDateTimeStates.includes(chosenActivityType || '');
    }, [chosenActivityType, requiredDateTimeStates]);

    const activityTypes = React.useMemo(() => {
        const types = state?.possible_state_transitions?.map((type) => {
            const approvalStatus = type.toUpperCase() as ApprovalStatus;
            return {
                value: approvalStatus,
                // APPROVED → "Enroll", REJECTED → "Reject", MEDICAL_CHECK →
                // "Medical Check": all route to a dedicated modal, not plain
                // status changes. The "Enrolled"/"Rejected"/"Medical check
                // scheduled" labels from getApprovalStatus are the
                // current-status pill wording and stay as-is elsewhere
                // (HWM-22859: the dropdown action reads "Medical Check").
                label:
                    approvalStatus === 'APPROVED'
                        ? 'Enroll'
                        : approvalStatus === 'REJECTED'
                          ? 'Reject'
                          : approvalStatus === 'MEDICAL_CHECK'
                            ? 'Medical Check'
                            : getApprovalStatus(approvalStatus).label
            };
        });

        const subTypes = state?.possible_sub_state_transitions_for_current_state?.map((type) => {
            const approvalSubStatus = type.toUpperCase() as AllApprovalStatusSubStates;
            return {
                value: approvalSubStatus,
                label: getApprovalSubStatus(approvalSubStatus, currentStateStatus).stageLabel
            };
        });

        // [HWM-22633 round 4e] Dedupe by label. The backend emits every
        // `ApprovalStatus` enum value in `possible_state_transitions`,
        // and both `INITIAL_INTEREST` and the legacy `INSURANCE_VALIDATION`
        // map to the same label ("Initial interest") via
        // `getApprovalStatus`. Without this filter the TYPE dropdown
        // renders "Initial interest" twice. More robust than hard-coding
        // an `INSURANCE_VALIDATION` exclusion — survives any future
        // shared-label additions.
        // [HWM-22813] "Expired" is a terminal status the backend reports in
        // possible_state_transitions, but agents must not manually expire a
        // lead from this dropdown — drop it from the options.
        const all = [...(types || []), ...(subTypes || [])].filter(
            ({ value }) => value !== 'EXPIRED'
        );

        // HWM-22633: Reject is always an available action from the Change
        // status modal (it routes to the reject confirmation modal). Guarantee
        // it's offered even when the backend's possible_state_transitions omits
        // "rejected" for the current state.
        if (!all.some(({ value }) => value === 'REJECTED')) {
            all.push({ value: 'REJECTED' as ApprovalStatus, label: 'Reject' });
        }

        // [HWM-22813] "Medical check missed". The backend's get_current_lead_state
        // never returns sub-status transitions, so this option is injected
        // client-side rather than relabelled. Only offered while the lead is in
        // the Medical Check stage — the backend promotes a bare MISSED_APPOINTMENT
        // sub-status to (MEDICAL_CHECK, MISSED_APPOINTMENT) but imposes no
        // source-state guard, so the dropdown is the gate. Selecting it submits
        // via approval_sub_status (the value is not in possible_state_transitions).
        if (
            currentStateStatus === 'MEDICAL_CHECK' &&
            !all.some(({ value }) => value === 'MISSED_APPOINTMENT')
        ) {
            all.push({
                value: 'MISSED_APPOINTMENT' as AllApprovalStatusSubStates,
                label: getApprovalStatus('MEDICAL_CHECK_MISSED').label
            });
        }

        const seen = new Set<string>();
        return all.filter(({ label }) => {
            if (seen.has(label)) return false;
            seen.add(label);
            return true;
        });
    }, [state, currentStateStatus]);

    const now = new Date();

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {activityTypes.length ? (
                <DefaultSelect
                    name="activity_type"
                    displayName="Type"
                    options={activityTypes.map(({ label, value }) => ({ label, value }))}
                />
            ) : null}
            {showDateAndTimeFields ? (
                <>
                    <Section
                        paddingLeft="none"
                        paddingBottom="small"
                        paddingTop="none"
                        paddingRight="none"
                    >
                        <Label htmlFor="date">DATE</Label>
                        <Controller
                            control={control}
                            name="schedule_date"
                            render={({ field: { onBlur, onChange } }) => (
                                <DatePicker
                                    onChange={(value: Date | null) => {
                                        // Clearing the field emits null; `format` throws on it.
                                        if (!value) return;
                                        setDate(value);
                                        onChange(format(value, 'P'));
                                    }}
                                    onBlur={onBlur}
                                    selected={date}
                                    minDate={now}
                                />
                            )}
                        />
                    </Section>
                    <Section
                        paddingLeft="none"
                        paddingBottom="small"
                        paddingTop="none"
                        paddingRight="none"
                        className={!date ? Style.disabled : ''}
                    >
                        <Label htmlFor="time">TIME</Label>
                        <Controller
                            control={control}
                            name="schedule_time"
                            render={({ field: { onBlur, onChange } }) => (
                                <DatePicker
                                    disabled={!date}
                                    selected={timeDate}
                                    onChange={(value: Date | null) => {
                                        if (!value) return;
                                        setTimeDate(value);
                                        onChange(format(value, 'pppp'));
                                    }}
                                    showTimeSelect
                                    showTimeSelectOnly
                                    timeIntervals={15}
                                    timeCaption="Time"
                                    dateFormat="h:mm aa"
                                    onBlur={onBlur}
                                    minTime={date && isToday(date) ? now : undefined}
                                    maxTime={
                                        date && isToday(date)
                                            ? setHours(setMinutes(now, 59), 23)
                                            : undefined
                                    }
                                />
                            )}
                        />
                    </Section>
                </>
            ) : null}
            <TextArea
                name="note"
                displayName="Note"
                placeholder="Add note"
                styles={{ minHeight: '24rem', width: '100%' }}
            />

            <div
                style={{
                    marginTop: 'auto',

                    minHeight: '5rem',
                    width: '100%'
                }}
            >
                <Button
                    type="submit"
                    width="full"
                    disabled={!isValid || isSubmitDisabled}
                    styles={{ marginBottom: '3rem' }}
                >
                    Submit
                </Button>
            </div>
        </div>
    );
};

export default FormInputs;
